import { supabase } from '@/integrations/supabase/client';
import { getAllCourses } from '@/data/courses';
import { Course } from '@/data/courses';
import { getAllCoursesUnified } from '@/utils/allCourses';

interface DatabaseCourse {
  id: string;
  title: string;
  description: string | null;
  category: string;
  icon: string | null;
  route: string | null;
  level: string;
  duration_hours: number;
  total_modules: number;
  is_active: boolean;
  prerequisites: string[];
  learning_outcomes: string[];
  metadata: any;
}

interface DatabaseModule {
  course_id: string;
  module_id: string;
  module_title: string;
  module_order: number;
  is_enabled: boolean;
  prerequisites: string[];
  estimated_duration: number | null;
  difficulty_level: string;
  module_config: any;
}

// Enhanced category mapping to cover all course types
const CATEGORY_MAPPING: Record<string, string> = {
  'sql': 'sql',
  'python': 'python',
  'ai': 'ai',
  'java': 'java',
  'dotnet': 'dotnet',
  'csharp': 'dotnet', // Map C# to .NET category
  'react': 'react',
  'aws': 'aws',
  'azure': 'azure',
  'gcp': 'gcp',
  'javascript': 'javascript',
  'ml': 'ai', // Map ML to AI category
  'datascience': 'data-science',
  'data-science': 'data-science',
  'dataengineering': 'data-engineering',
  'data-engineering': 'data-engineering',
  'powerbi': 'powerbi',
  'tableau': 'tableau',
  'devops': 'devops',
  'appsec': 'security',
  'mobile': 'mobile',
  'backend': 'backend',
  'database': 'database',
  'security': 'security',
  'blockchain': 'blockchain',
  'enterprise-training': 'enterprise',
  'professional-development': 'professional-dev',
  'game-development': 'game-dev',
  'ui-ux': 'ui-ux',
  'graphics': 'graphics',
  'graphics-programming': 'graphics',
  'etl': 'etl',
  'rpa': 'automation',
  'infrastructure': 'infrastructure',
  'frontend-advanced': 'frontend',
  'testing-modern': 'testing',
  'testing': 'testing',
  'web3-development': 'blockchain',
  'serverless-edge': 'cloud',
  'specialized-ai': 'specialized-ai' // Specialized AI platforms
};

export class CourseMigrationService {
  // Normalize level values to match database constraints
  private static normalizeLevel(level?: string): string {
    if (!level) return 'intermediate';
    
    const normalized = level.toLowerCase().trim();
    
    // Map various level names to valid values
    const levelMap: Record<string, string> = {
      'beginner': 'beginner',
      'fundamentals': 'beginner',
      'basic': 'beginner',
      'intermediate': 'intermediate',
      'advanced': 'advanced',
      'expert': 'advanced',
      'master': 'advanced'
    };
    
    return levelMap[normalized] || 'intermediate';
  }
  
  // Normalize difficulty values for modules
  private static normalizeDifficulty(difficulty?: string): string {
    if (!difficulty) return 'intermediate';
    
    const normalized = difficulty.toLowerCase().trim();
    
    // Map various difficulty names to valid values
    const difficultyMap: Record<string, string> = {
      'beginner': 'beginner',
      'easy': 'beginner',
      'intermediate': 'intermediate',
      'medium': 'intermediate',
      'advanced': 'advanced',
      'hard': 'advanced',
      'expert': 'advanced'
    };
    
    return difficultyMap[normalized] || 'intermediate';
  }

  static async validateCategories(): Promise<string[]> {
    const { data: categories, error } = await supabase
      .from('course_categories')
      .select('id')
      .in('id', Object.values(CATEGORY_MAPPING));

    if (error) {
      throw new Error(`Failed to fetch categories: ${error.message}`);
    }

    return categories?.map(c => c.id) || [];
  }

  static async migrateCourse(course: Course): Promise<{ success: boolean; error?: string }> {
    try {
      // Enhanced category detection
      const categoryId = this.determineCourseCategory(course);
      if (!categoryId) {
        return { success: false, error: `Unable to determine category for course: ${course.id}` };
      }

      // Extract duration from totalDuration string
      const durationMatch = course.totalDuration.match(/(\d+)/);
      const durationHours = durationMatch ? parseInt(durationMatch[1]) : 0;

      // Prepare course data for database
      const dbCourse: DatabaseCourse = {
        id: course.id,
        title: course.title,
        description: course.description || null,
        category: categoryId,
        icon: null, // Will be set based on category
        route: `/courses/${course.id}`,
        level: this.normalizeLevel(course.level),
        duration_hours: durationHours,
        total_modules: course.modules?.length || 0,
        is_active: true,
        prerequisites: course.prerequisites || [],
        learning_outcomes: course.learningOutcomes || [],
        metadata: {
          migrated_at: new Date().toISOString(),
          source: 'legacy_system',
          original_type: course.type,
          image: course.image
        }
      };

      // Insert course
      const { data: courseData, error: courseError } = await supabase
        .from('courses')
        .upsert(dbCourse)
        .select()
        .single();

      if (courseError) {
        return { success: false, error: `Course insert failed: ${courseError.message}` };
      }

      // Migrate modules - ALWAYS use real module IDs, never generate fake ones
      if (course.modules && course.modules.length > 0) {
        const { generateModuleId, sanitizeModuleTitle } = await import('@/utils/moduleIdGenerator');
        
        const modules: DatabaseModule[] = course.modules.map((module, index) => {
          // Use existing module ID if present, otherwise generate meaningful ID from title
          const moduleId = module.id || generateModuleId(course.id, sanitizeModuleTitle(module.title));
          
          return {
            course_id: course.id,
            module_id: moduleId,
            module_title: module.title || `Module ${index + 1}`,
            module_order: index + 1,
            is_enabled: true,
            prerequisites: module.prerequisites || [],
            estimated_duration: this.extractDurationMinutes(module.duration || module.estimatedTime),
            difficulty_level: this.normalizeDifficulty(module.difficulty),
            module_config: {
              content: module.content,
              objectives: module.objectives || module.learningObjectives || [],
              quiz: module.quiz,
              exercises: module.exercises,
              codeExamples: module.codeExamples,
              migrated_from: 'legacy_system'
            }
          };
        });

        const { error: modulesError } = await supabase
          .from('course_modules')
          .upsert(modules);

        if (modulesError) {
          console.warn(`Module migration failed for course ${course.id}:`, modulesError.message);
        }
      }

      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  static extractDurationMinutes(duration?: string): number | null {
    if (!duration) return null;
    
    const hourMatch = duration.match(/(\d+)\s*hours?/i);
    const minMatch = duration.match(/(\d+)\s*min/i);
    
    if (hourMatch) {
      return parseInt(hourMatch[1]) * 60;
    } else if (minMatch) {
      return parseInt(minMatch[1]);
    }
    
    return null;
  }

  static async migrateAllCourses(): Promise<{
    total: number;
    successful: number;
    failed: number;
    errors: string[];
  }> {
    console.log('🚀 Starting automated course migration...');
    
    try {
      // Get all courses from unified system
      const allCourses = getAllCoursesUnified();
      
      // Call the edge function to handle the bulk migration
      const { data, error } = await supabase.functions.invoke('migrate-courses', {
        body: { courses: allCourses }
      });

      if (error) {
        // Silent fail - return error data without console logging
        return {
          total: 0,
          successful: 0,
          failed: 1,
          errors: [`Edge function error: ${error.message}`]
        };
      }

      if (data.success) {
        console.log('✅ Migration completed successfully');
        console.log('📊 Results:', data);
        
        return {
          total: data.successCount + data.errorCount,
          successful: data.successCount,
          failed: data.errorCount,
          errors: data.errors || []
        };
      } else {
        console.error('❌ Migration failed:', data.error);
        return {
          total: 0,
          successful: 0,
          failed: 1,
          errors: [data.error]
        };
      }
    } catch (error) {
      console.error('Migration service error:', error);
      return {
        total: 0,
        successful: 0,
        failed: 1,
        errors: [error instanceof Error ? error.message : 'Unknown error']
      };
    }
  }

  static async getCourseStats(): Promise<{
    totalCourses: number;
    coursesByCategory: Record<string, number>;
    totalModules: number;
  }> {
    const { data: courses, error } = await supabase
      .from('courses')
      .select('category, total_modules');

    if (error) {
      throw new Error(`Failed to get course stats: ${error.message}`);
    }

    const stats = {
      totalCourses: courses?.length || 0,
      coursesByCategory: {} as Record<string, number>,
      totalModules: 0
    };

    courses?.forEach(course => {
      stats.coursesByCategory[course.category] = 
        (stats.coursesByCategory[course.category] || 0) + 1;
      stats.totalModules += course.total_modules || 0;
    });

    return stats;
  }

  static determineCourseCategory(course: Course): string {
    // Check explicit type field
    if (course.type && CATEGORY_MAPPING[course.type]) {
      return CATEGORY_MAPPING[course.type];
    }
    
    // Check course ID for category hints
    const courseId = course.id.toLowerCase();
    for (const [key, value] of Object.entries(CATEGORY_MAPPING)) {
      if (courseId.includes(key)) {
        return value;
      }
    }
    
    // Check title for category hints
    const title = course.title?.toLowerCase() || '';
    if (title.includes('python')) return 'python';
    if (title.includes('sql')) return 'sql';
    if (title.includes('java')) return 'java';
    if (title.includes('c#') || title.includes('csharp')) return 'dotnet';
    if (title.includes('react')) return 'react';
    if (title.includes('aws')) return 'aws';
    if (title.includes('azure')) return 'azure';
    if (title.includes('ai') || title.includes('machine learning')) return 'ai';
    if (title.includes('data science')) return 'data-science';
    if (title.includes('devops')) return 'devops';
    if (title.includes('security')) return 'security';
    if (title.includes('mobile')) return 'mobile';
    if (title.includes('game')) return 'game-dev';
    if (title.includes('blockchain') || title.includes('web3')) return 'blockchain';
    
    // Default fallback
    return 'programming';
  }
}

export default CourseMigrationService;